/**
 * Footer Component
 *
 * Reusable footer for public pages with company, quick links, support, and legal columns.
 */

import {
  Box,
  Container,
  Typography,
  Link as MuiLink,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const FOOTER_LINKS = {
  company: [
    { label: 'List Your Venue', href: '/list-your-venue' },
    { label: 'Contact Us', href: '/contact' },
  ],
  quickLinks: [
    { label: 'Browse Venues', href: '/venues' },
    { label: 'How It Works', href: '/#how-it-works' },
    { label: 'Loyalty Program', href: '/loyalty' },
    { label: 'FAQs', href: '/#faq' },
  ],
  support: [{ label: 'Help Center', href: '/contact' }],
  legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Refund Policy', href: '/refund-policy' },
  ],
};

const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    href: 'https://instagram.com/lordofvenues',
    Icon: InstagramIcon,
  },
  {
    label: 'Twitter / X',
    href: 'https://twitter.com/lordofvenues',
    Icon: TwitterIcon,
  },
  {
    label: 'LinkedIn',
    href: 'https://linkedin.com/company/lordofvenues',
    Icon: LinkedInIcon,
  },
];

interface FooterLinkProps {
  label: string;
  href: string;
  isExternal?: boolean;
  isAnchor?: boolean;
}

function FooterLink({ label, href, isExternal, isAnchor }: FooterLinkProps) {
  const sx = {
    display: 'block',
    color: 'text.secondary',
    textDecoration: 'none',
    mb: 1,
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontSize: '0.9375rem',
    transition: 'color 0.2s',
    '&:hover': {
      color: 'primary.main',
    },
  };

  if (isExternal) {
    return (
      <MuiLink href={href} target="_blank" rel="noopener noreferrer" sx={sx}>
        {label}
      </MuiLink>
    );
  }

  if (isAnchor) {
    return (
      <MuiLink href={href} sx={sx}>
        {label}
      </MuiLink>
    );
  }

  return (
    <MuiLink component={Link} to={href} sx={sx}>
      {label}
    </MuiLink>
  );
}

function FooterColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        color: 'text.primary',
        fontFamily: '"IBM Plex Mono", monospace',
        fontWeight: 700,
        fontSize: '0.8125rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        pt: 8,
        pb: 4,
        px: 2,
        backgroundColor: 'background.default',
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        {/* Top grid: brand + 4 link columns */}
        <Grid container spacing={4}>
          {/* Brand column */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: 'text.primary',
                mb: 1,
              }}
            >
              Lord of Venues
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontStyle: 'italic',
                mb: 3,
              }}
            >
              Book. Play. Repeat.
            </Typography>

            {/* Social icons */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <IconButton
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Company column */}
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumnHeading>Company</FooterColumnHeading>
            {FOOTER_LINKS.company.map((link) => (
              <FooterLink key={link.href} {...link} />
            ))}
          </Grid>

          {/* Quick Links column */}
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <FooterColumnHeading>Quick Links</FooterColumnHeading>
            {FOOTER_LINKS.quickLinks.map((link) => (
              <FooterLink key={link.href} {...link} isAnchor={link.href.startsWith('/#')} />
            ))}
          </Grid>

          {/* Support column */}
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumnHeading>Support</FooterColumnHeading>
            {FOOTER_LINKS.support.map((link) => (
              <FooterLink key={link.href} {...link} />
            ))}
          </Grid>

          {/* Legal column */}
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumnHeading>Legal</FooterColumnHeading>
            {FOOTER_LINKS.legal.map((link) => (
              <FooterLink key={link.href} {...link} />
            ))}
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Bottom bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontFamily: '"IBM Plex Sans", sans-serif',
            }}
          >
            &copy; {currentYear} Lord of Venues. All rights reserved.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontSize: '0.8125rem',
            }}
          >
            Made with care in India
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
